import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { ReleasesService } from 'app/modules/user/releases/releases.service';
import { Artist, ArtistSearchResult, Release } from 'app/modules/user/releases/releases.types';
import { debounceTime, Observable, of, Subject, Subscription, take, takeUntil } from 'rxjs';

@Component({
    selector: 'app-new-artist-modal',
    templateUrl: './new-artist-modal.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./new-artist-modal.scss'],
})
export class NewArtistModalComponent implements OnInit, OnDestroy {
    form: FormGroup;
    thisUser: User;
    forUserUuid: string;
    spotifyArtists: ArtistSearchResult[] = [];
    appleArtists: ArtistSearchResult[] = [];
    isLoading = false;
    errorKey: string;
    existingArtist?: Artist;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _spotifySubscription: Subscription;
    private _appleSubscription: Subscription;
    @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

    constructor(
        public matDialogRef: MatDialogRef<NewArtistModalComponent>,
        private _formBuilder: FormBuilder,
        public userService: UserService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _releasesService: ReleasesService,
        @Inject(MAT_DIALOG_DATA) public data: any,
    ) {
    }

    ngOnInit(): void {
        this.existingArtist = this.data?.artist;
        this.forUserUuid = this.data?.userUuid;

        this.form = this._formBuilder.group({
            uuid: [null],
            title: ['', Validators.required],
            email: [null],
            spotify: ['', this.validateSpotify],
            apple: ['', this.validateApple],
            amazon: [null, this.validateAmazon],
            isni: [null, this.validateISNI],
            soundcloud: [null],
            meta: [null],
            newArtist: [false],
        });

        this.form.get('title').patchValue(this.data.title);

        if (this.existingArtist) {
            this.form.get('uuid').patchValue(this.existingArtist.uuid);
            this.form.get('title').patchValue(this.existingArtist.title);
            this.form.get('email').patchValue(this.existingArtist.email);
            this.form.get('spotify').patchValue(this.existingArtist.spotifyId);
            this.form.get('apple').patchValue(this.existingArtist.appleId);
            this.form.get('amazon').patchValue(this.existingArtist.amazonId);
            this.form.get('isni').patchValue(this.existingArtist.isni);
            this.form.get('soundcloud').patchValue(this.existingArtist.soundcloudId);
            this.form.get('meta').patchValue(this.existingArtist.metaId);
        }

        this.userService.user$
            .pipe(take(1))
            .subscribe((user: User) => {
                this.thisUser = user;
                this._changeDetectorRef.markForCheck();
            });

        this.form.get('spotify').valueChanges.pipe(
            takeUntil(this._unsubscribeAll),
            debounceTime(300)
        ).subscribe(name => {
            if (!this.form.get('spotify').valid) {
                this.subscribeToObservable(this._spotifySubscription, this.updateSpotifyArtists, this._releasesService.searchArtist(name, "spotify"));
            }
        });

        this.form.get('apple').valueChanges.pipe(
            takeUntil(this._unsubscribeAll),
            debounceTime(300)
        ).subscribe(name => {
            if (!this.form.get('apple').valid) {
                this.subscribeToObservable(this._appleSubscription, this.updateAppleArtists, this._releasesService.searchArtist(name, "apple"));
            }
        });

        this._changeDetectorRef.markForCheck();
    }

    subscribeToObservable(currentSubscription: Subscription, handler: Function, serviceObservable: Observable<any>) {
        if (currentSubscription && !currentSubscription.closed) {
            currentSubscription.unsubscribe();
        }

        this.isLoading = true;
        currentSubscription = serviceObservable
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: handler.bind(this),
                error: (error) => {
                    console.error(error);
                    this.isLoading = false;
                    return of(true);
                }
            });
    }

    onSpotifySearchFocused() {
        let value = this.form.get('spotify').value;
        if (!value && !this.spotifyArtists?.length && this.form.get('title').valid) {
            this.subscribeToObservable(this._spotifySubscription, this.updateSpotifyArtists, this._releasesService.searchArtist(this.form.get('title').value, "spotify"));
        }
    }

    onAppleSearchFocused() {
        let value = this.form.get('apple').value;
        if (!value && !this.appleArtists?.length && this.form.get('title').valid) {
            this.subscribeToObservable(this._appleSubscription, this.updateAppleArtists, this._releasesService.searchArtist(this.form.get('title').value, "apple"));
        }
    }

    selectArtist($event) {
        console.log($event);
    }

    openSpotifyArtistPage(event, artistId: string) {
        event.stopPropagation();
        event.preventDefault();
        window.open(`https://open.spotify.com/artist/${artistId}`, '_blank');
    }

    openAppleArtistPage(event, artistId: string) {
        event.stopPropagation();
        event.preventDefault();
        window.open(`https://music.apple.com/artist/${artistId}`, '_blank');
    }

    updateSpotifyArtists(artists: ArtistSearchResult[]) {
        console.log("updateSpotifyArtists", artists);
        this.spotifyArtists = artists;
        this._changeDetectorRef.markForCheck();
    }

    updateAppleArtists(artists: ArtistSearchResult[]) {
        console.log("updateAppleArtists", artists);
        this.appleArtists = artists;
        this._changeDetectorRef.markForCheck();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    onCheckboxChange() {
        this.form.get('spotify').updateValueAndValidity();
        this.form.get('apple').updateValueAndValidity();
    }

    searchArtist(name: string, store: string) {
        console.log("searchArtist", name, store)
        /* this._releasesService.searchArtist(name, store).subscribe(
            artists => {
                console.log("artists", artists);
            }
        ); */
    }

    validateSpotify = (control: FormControl): ValidationErrors | null => {
        //Example url of artist page
        //https://open.spotify.com/artist/1aK2PiGHyEgc4wAIKQzMj2?si=esZ9k-C5RsqjSsApSkO2tw

        if (control.value && control.value.match(/https:\/\/open\.spotify\.com\/artist\/[a-zA-Z0-9]+/)) {
            this.autocomplete?.closePanel();
            return null;
        }

        if (control.value && control.value.match(/spotify:artist:[a-zA-Z0-9]+/)) {
            this.autocomplete?.closePanel();
            return null;
        }

        if (control.value && control.value.trim().match(/^[a-zA-Z0-9]{20,24}$/)) {
            this.autocomplete?.closePanel();
            return null;
        }

        if (this.form?.get('newArtist')?.value) {
            this.autocomplete?.closePanel();
            return null;
        }
        return { invalidSpotifyUrl: true };
    }

    validateApple = (control: FormControl): ValidationErrors | null => {
        //Example url of artist page
        //https://music.apple.com/us/artist/rod-wave/1140623439

        if (control.value && control.value.match(/music\.apple\.com\/[a-zA-Z]{2}\/artist\/[^\/]+\/([0-9]+)/)) {
            return null;
        }

        if (control.value && control.value.match(/music\.apple\.com\/[a-zA-Z]{2}\/artist\/([0-9]+)/)) {
            return null;
        }

        if (control.value && control.value.match(/^[0-9]+$/)) {
            return null;
        }

        if (this.form?.get('newArtist')?.value) {
            return null;
        }
        return { invalidAppleUrl: true };
    }

    validateISNI = (control: FormControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        // The FAQ of the isni.org websites states "An ISNI is made up of 16 digits, the last character being a check character." ISNI consists of 15 digits followed by a check character. The check character may be either a decimal digit or the character "X".
        if (control.value && control.value.replaceAll(" ", "").match(/^[0-9]{15}[0-9X]$/)) {
            return null;
        }

        return { invalidISNI: true };
    }

    validateAmazon = (control: FormControl): ValidationErrors | null => {
        //Es handelt sich dabei um eine 10-stellige alphanumerische Produktinformationsnummer

        if (!control.value) {
            return null;
        }

        if (control.value && control.value.match(/^[a-zA-Z0-9]{10}$/)) {
            return null;
        }

        return { invalidAmazon: true };
    }

    async onSubmit() {
        let data = this.form.getRawValue();
        let artist: Artist = {
            uuid: data.uuid,
            title: data.title,
            email: data.email,
            spotifyId: data.spotify,
            appleId: data.apple,
            amazonId: data.amazon,
            isni: data.isni,
            soundcloudId: data.soundcloud,
            metaId: data.meta,
        }
        this._releasesService.createArtist(artist, this.forUserUuid ?? undefined).subscribe({
            next: (createdArtist: Artist) => this.matDialogRef.close(createdArtist),
            error: (error) => {
                console.error(error);
                this.errorKey = error.error?.message ?? "error-unknown";
                this._changeDetectorRef.markForCheck();
            }
        });
    }
}
